import { useEffect, useRef } from "react";
import "./TrustedByBand.css";

// v49 — client logos for the homepage "Trusted by" band. Add a client by
// adding one entry here (white + colour files in /public/clients).
export const TRUSTED_CLIENTS = [
  { key: "coinswitch", name: "CoinSwitch", white: "/clients/coinswitch-white.webp", colour: "/clients/coinswitch-colour.webp" },
];

const LABEL = "TRUSTED BY";
const SWEEP_S = 2.6;

function ClientLogo({ c }) {
  return (
    <div
      className="kr-tb-logo"
      data-testid={`trusted-by-logo-${c.key}`}
      onAnimationEnd={(e) => { if (e.animationName === "kr-tb-dots") e.currentTarget.classList.add("is-built"); }}
    >
      <img className="kr-tb-white" src={c.white} alt={c.name} width="420" height="61" decoding="async" />
      <img className="kr-tb-colour" src={c.colour} alt="" aria-hidden="true" width="420" height="61" decoding="async" />
      <span className="kr-tb-ping" aria-hidden="true" />
    </div>
  );
}

export function TrustedByBand({ clients = TRUSTED_CLIENTS }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // Time each logo's flash to the moment the beam crosses its centre.
    const place = () => {
      const w = el.offsetWidth || 1;
      const left = el.getBoundingClientRect().left;
      el.querySelectorAll(".kr-tb-logo").forEach((logo) => {
        const r = logo.getBoundingClientRect();
        const frac = Math.min(1, Math.max(0, (r.left - left + r.width / 2) / w));
        // Beam centre travels from -10% to 110% of the band in SWEEP_S.
        logo.style.setProperty("--kr-tb-hit", `${(((frac + 0.1) / 1.2) * SWEEP_S).toFixed(2)}s`);
      });
    };
    place();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(place) : null;
    if (ro) ro.observe(el);
    // Intro on first view; the loop only runs while the band is on screen.
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) el.classList.add("is-in");
      el.classList.toggle("is-live", e.isIntersecting);
    }, { threshold: 0.35 });
    io.observe(el);
    return () => { io.disconnect(); if (ro) ro.disconnect(); };
  }, [clients.length]);

  return (
    <section ref={ref} className="kr-tb relative z-10" data-testid="trusted-by-band" aria-label="Trusted by">
      <span className="kr-tb-line kr-tb-line-top" aria-hidden="true" />
      <span className="kr-tb-beam" aria-hidden="true" />
      <div className="kr-tb-inner">
        <div className="kr-tb-label mono text-[10px] uppercase tracking-[0.35em] text-zinc-500" data-testid="trusted-by-label" aria-label="Trusted by">
          {LABEL.split("").map((ch, i) => (
            <span key={i} aria-hidden="true" style={{ animationDelay: `${0.35 + i * 0.05}s` }}>{ch === " " ? "\u00A0" : ch}</span>
          ))}
        </div>
        <span className="kr-tb-divider" aria-hidden="true" />
        <div className="kr-tb-logos">
          {clients.map((c) => <ClientLogo key={c.key} c={c} />)}
        </div>
      </div>
      <span className="kr-tb-line kr-tb-line-bottom" aria-hidden="true" />
    </section>
  );
}

export default TrustedByBand;
